import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import { selectLoggedIn } from 'selectors/accountSelectors'
import useLoyaltyChooseDoubleRewards from '../hooks/useLoyaltyChooseDoubleRewards'
import { useAppSelector } from 'hooks/reduxHooks'
import LayoutContainer from 'components/Common/LayoutContainer'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import Pane from 'components/Common/Pane'
import { mediaQueryUp } from 'components/utils/breakpoint'
import TextLink from 'LuxKit/TextLink'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import type { ReactNode } from 'react'
import { useCallback } from 'react'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import { tierDarkBackgroundStyle, tierDarkContrastColorStyle } from 'styles/utils/tierStyles'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'

const Grid = styled.div`
  display: grid;
  gap: ${rem(20)};
  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(3, 1fr);

    &.two-steps {
      grid-template-columns: repeat(2, 1fr);
    }
  }
`

const Card = styled(Pane)`
  ${mediaQueryUp.tablet} {
    min-height: ${rem(180)};
  }
`

const Number = styled(Group)`
  width: ${rem(40)};
  height: ${rem(40)};
  flex-shrink: 0;
  border-radius: 50%;
  ${tierDarkBackgroundStyle}
  ${tierDarkContrastColorStyle}
`

interface Props {
  onClickSharedCta: () => void
  cta: ReactNode
}

function LoyaltyChooseDoubleRewardsLandingPageHowItWorks({ onClickSharedCta, cta }: Props) {
  const intl = useIntl()
  const programName = getLoyaltyProgramDisplayName()
  const loggedIn = useAppSelector(selectLoggedIn)
  const hasAccount = useAppSelector(getHasLoyaltyAccount)
  const { memberState, endsAt } = useLoyaltyChooseDoubleRewards()
  const tier = useAppSelector(getLoyaltyDisplayTier)

  const isMember = loggedIn && hasAccount
  const showStatusCredits = tier !== 'infinite'
  const selected = isMember && memberState.status === 'enrolled' ? memberState.choice : undefined

  // Only use shared cta when user is not enrolled
  const renderJoinLink = useCallback(
    (chunks: ReactNode) => (
      <TextLink key="join" size="medium" onClick={onClickSharedCta}>
        {chunks}
      </TextLink>
    ),
    [onClickSharedCta],
  )

  const chooseBothDescription = intl.formatMessage({
    id: 'YgxNJSB4pYDThAkAU25y',
    defaultMessage: 'Choose double {programName} Points or double Status Credits.',
    description: 'First step describing the available double rewards campaign choices',
  }, { programName })

  const choosePointsDescription = intl.formatMessage({
    id: '8gIeL8uhUK7bXcc515jn',
    defaultMessage: 'Choose double {programName} Points.',
    description: 'First campaign step describing the points choice for an Infinite member',
  }, { programName })

  const chooseGuestDescription = intl.formatMessage(
    {
      id: 'PwvBJdMug1UHMoe7rtVL',
      defaultMessage:
        'Choose double {programName} Points or double Status Credits. <join>Join {programName} for free today.</join>',
      description: 'First step for guests and nonmembers with a link to join the loyalty program for free',
    },
    { programName, join: renderJoinLink },
  )

  const pointsActivatedDescription = intl.formatMessage({
    id: 'SUObrdB79zWJGTP_TZLX',
    defaultMessage: 'You have activated double {programName} Points',
    description: 'First campaign step confirming the member has chosen double loyalty points',
  }, { programName })

  const creditsActivatedDescription = intl.formatMessage({
    id: 'KtIfwIV6h2Cnpwbj5yaE',
    defaultMessage: 'You have activated double Status Credits',
    description: 'First campaign step confirming the member has chosen double Status Credits',
  })

  let chooseDescription: ReactNode = showStatusCredits ? chooseBothDescription : choosePointsDescription
  if (!isMember) {
    chooseDescription = chooseGuestDescription
  } else if (selected === 'points') {
    chooseDescription = pointsActivatedDescription
  } else if (selected === 'statusCredits' && showStatusCredits) {
    chooseDescription = creditsActivatedDescription
  }

  const enjoyBothDescription = intl.formatMessage({
    id: 'TSgTL66NQgpHTYariQ6v',
    defaultMessage: 'After you travel, we’ll add your bonus Points or Status Credits to your account.',
    description: 'Third step explaining when double rewards are added to the loyalty account',
  })

  const enjoyPointsDescription = intl.formatMessage({
    id: 'zfVJtesO4JwD9fZZRcou',
    defaultMessage: 'After you travel, we’ll add your bonus {programName} Points to your account.',
    description: 'Third campaign step explaining when the selected bonus loyalty points are credited',
  }, { programName })

  const enjoyCreditsDescription = intl.formatMessage({
    id: 'ZATog3u9CNGAWR3oiJP5',
    defaultMessage: 'After you travel, we’ll add your bonus Status Credits to your account.',
    description: 'Third campaign step explaining when the selected bonus Status Credits are credited',
  })

  let enjoyDescription = showStatusCredits ? enjoyBothDescription : enjoyPointsDescription
  if (selected === 'points') {
    enjoyDescription = enjoyPointsDescription
  }
  if (selected === 'statusCredits' && showStatusCredits) {
    enjoyDescription = enjoyCreditsDescription
  }

  const bookWithoutDate = intl.formatMessage({
    id: 'I4wmn73RNW-XApMnHpfF',
    defaultMessage: 'Book an eligible hotel, tour or cruise. Your reward applies to every eligible booking.',
    description: 'Second campaign step when the booking deadline has not been configured yet',
  })

  const bookWithDate = endsAt && intl.formatMessage(
    {
      id: 'yL-9MZdduZxseDL5eSli',
      defaultMessage: 'Book an eligible hotel, tour or cruise. Your reward applies to every eligible booking until {date}.',
      description: 'Second campaign step describing eligible bookings and the booking deadline',
    },
    { date: <FormatDateTime value={endsAt} day="numeric" month="long" /> },
  )

  const steps = [
    {
      key: 'choose',
      title: intl.formatMessage({
        id: 'FAU4x5oJisfh8M6Z1wmY',
        defaultMessage: 'Choose your reward',
        description: 'First step heading for the double rewards campaign how it works section',
      }),
      description: chooseDescription,
    },
    {
      key: 'book',
      title: intl.formatMessage({
        id: 'SbH5M57fwXR9L0BVbJU4',
        defaultMessage: 'Book your escape',
        description: 'Second step heading for the double rewards campaign how it works section',
      }),
      description: bookWithDate ?? bookWithoutDate,
    },
    {
      key: 'enjoy',
      title: intl.formatMessage({
        id: 'p2aklvxI2zoNDwT78hNC',
        defaultMessage: 'Enjoy your reward',
        description: 'Third step heading for the double rewards campaign how it works section',
      }),
      description: enjoyDescription,
    },
  ]

  const heading = intl.formatMessage({
    id: 'ySB17GsuHFqp_0jX9tQK',
    defaultMessage: 'How it works',
    description: 'Heading for the steps explaining the double rewards campaign on its landing page',
  })

  return (
    <Sheet as="section" padding="56 0">
      <LayoutContainer variant="medium">
        <Group direction="vertical" gap={20} horizontalAlign="center">
          <Heading variant="heading2" align="center">
            {heading}
          </Heading>
          <Grid className={tier === 'infinite' ? 'two-steps' : undefined}>
            {steps.filter(step => tier !== 'infinite' || step.key !== 'choose').map((step, index) => (
              <Card key={step.key} background="none" border="light-foreground" borderRadius="L" shadow="bottom-medium" padding={20}>
                <Group
                  direction="horizontal"
                  tabletDirection="vertical"
                  verticalAlign="center"
                  tabletVerticalAlign="start"
                  tabletHorizontalAlign="start"
                  gap={16}
                >
                  <Number className={`tier-${tier}`} direction="horizontal" horizontalAlign="center" verticalAlign="center">
                    <BodyText variant="large" weight="bold" colour="inherit">
                      {intl.formatNumber(index + 1)}
                    </BodyText>
                  </Number>
                  <Group direction="vertical" gap={4}>
                    <BodyText variant="large" weight="bold">
                      {step.title}
                    </BodyText>
                    <BodyText variant="medium" colour={selected && step.key === 'choose' ? 'success-dark' : undefined}>
                      {step.description}
                    </BodyText>
                  </Group>
                </Group>
              </Card>
            ))}
          </Grid>
          {cta}
        </Group>
      </LayoutContainer>
    </Sheet>
  )
}

export default LoyaltyChooseDoubleRewardsLandingPageHowItWorks
